import { Routes } from '@angular/router';
import { needs } from '@core/config/workspace-route.util';
import { personaMatch, typeMatch } from '@core/guards/workspace-match.guard';

/**
 * Business workspace routes (staff persona).
 *
 * For paths shared with education (`assessments`, `campaigns`, `evaluation`,
 * `exports`, home), these entries are the unguarded defaults and MUST be
 * registered after `EDUCATION_WORKSPACE_ROUTES` so education's `canMatch`
 * wins when the active workspace is education.
 */
export const BUSINESS_WORKSPACE_ROUTES: Routes = [
  // ── Home ───────────────────────────────────────────────────────────────────
  {
    path: '',
    pathMatch: 'full',
    canMatch: [personaMatch('businessStaff')],
    loadComponent: () =>
      import('../../pages/workspaces/business/home/home.page').then(
        (m) => m.HomePage,
      ),
  },

  // ── Assessments ────────────────────────────────────────────────────────────
  {
    path: 'assessments',
    ...needs('assessment.edit', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/studio',
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/templates',
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/templates/assessment-templates.page'
      ).then((m) => m.AssessmentTemplatesPage),
  },
  // The studio is the assessment detail view in this workspace — one surface
  // for structure, questions and settings. Kept as a redirect for old links.
  {
    path: 'assessments/detail',
    redirectTo: 'assessments/studio',
  },
  // Participant preview is the real player in read-only mode (`attempt/preview`),
  // not a separate mock screen. Kept as a redirect so old links keep working.
  {
    path: 'assessments/preview',
    redirectTo: 'assessments/taking',
  },
  {
    path: 'assessments/versions',
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/taking',
    ...needs('assessment.view'),
    loadComponent: () =>
      import(
        '../../pages/assessments/campaigns/assigned-assessments.page'
      ).then((m) => m.AssignedAssessmentsPage),
  },

  // ── Campaigns ──────────────────────────────────────────────────────────────
  {
    path: 'campaigns',
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/launch',
    ...needs('campaign.create', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/monitor',
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/participants',
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/results',
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/results/individual',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'campaigns/audit-trail',
    loadComponent: () =>
      import(
        '../../pages/workspaces/business/campaigns/audit-trail/audit-trail.page'
      ).then((m) => m.AuditTrailPage),
  },
  {
    path: 'campaigns/evidence-export',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'campaigns/reminders',
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },

  // ── Training ───────────────────────────────────────────────────────────────
  // Business-only. `typeMatch('business')` keeps the compliance-training
  // surface out of education / creator / learner workspaces, which distribute
  // assessments by assignment rather than by tokenised landing link.
  //
  // There is no training *board*: programs are authored in the library beside
  // the assessments and tracked on the delivery board beside the campaigns, so
  // nobody has to know which mechanic a piece of content uses before finding
  // Training programs are authored in the assessment studio (Program tab for
  // rules + materials). `/training/builder` redirects there so old bookmarks
  // keep working. Results stay on their own page; the public landing is
  // `training/:token` at the root.
  // No `canMatch` here: Angular rejects a route that carries both a redirect and
  // a guard (NG04014), and the redirect is harmless in the other workspace types
  // — none of them registers `training`, so they would 404 instead.
  {
    path: 'training',
    pathMatch: 'full',
    redirectTo: 'campaigns',
  },
  {
    path: 'training/builder',
    canMatch: [typeMatch('business')],
    ...needs('campaign.manage', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },

  // ── Evaluation ─────────────────────────────────────────────────────────────
  {
    path: 'evaluation',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'evaluation/review',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'evaluation/history',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'evaluation/ai-grading',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },

  // ── Groups (Teams / Departments / Branches) ────────────────────────────────
  // Same backend Group entity education renders as Classes; the wording follows
  // the organization type. `typeMatch('business')` keeps education on its own
  // richer Classes pages.
  {
    path: 'groups',
    canMatch: [typeMatch('business')],
    ...needs('classes.view'),
    loadComponent: () =>
      import('../../pages/workspaces/business/groups/groups.page').then(
        (m) => m.BusinessGroupsPage,
      ),
  },
  {
    path: 'groups/detail',
    canMatch: [typeMatch('business')],
    ...needs('classes.view'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/business/groups/detail/group-detail.page'
      ).then((m) => m.BusinessGroupDetailPage),
  },
  // Mirror of education's `/groups` → Classes alias: a workspace that switches
  // its organization type keeps working from links minted under the other
  // vocabulary. (redirectTo + canMatch is illegal — NG04014 — so load directly.)
  {
    path: 'classes',
    canMatch: [typeMatch('business')],
    ...needs('classes.view'),
    loadComponent: () =>
      import('../../pages/workspaces/business/groups/groups.page').then(
        (m) => m.BusinessGroupsPage,
      ),
  },
  {
    path: 'classes/detail',
    canMatch: [typeMatch('business')],
    ...needs('classes.view'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/business/groups/detail/group-detail.page'
      ).then((m) => m.BusinessGroupDetailPage),
  },

  // ── Exports ────────────────────────────────────────────────────────────────
  {
    path: 'exports',
    ...needs('campaign.manage'),
    loadComponent: () =>
      import('../../pages/workspaces/shared/exports/exports.page').then(
        (m) => m.ExportsPage,
      ),
  },
];
